import React from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { X, Plane, CheckCircle2 } from 'lucide-react';

export default function FlightListNew({ flights, onDeleteFlight, onFlightFlown, loading, isLoggedIn = false }) {
  if (loading) {
    return (
      <div className="space-y-2 p-4">
        <div className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wide">
          Loading flights...
        </div>
        {[1, 2, 3].map((i) => (
          <Card key={i} className="p-2 animate-pulse">
            <div className="h-4 bg-muted rounded w-24 mb-2"></div>
            <div className="flex gap-1.5">
              <div className="h-5 bg-muted rounded w-16"></div>
              <div className="h-5 bg-muted rounded w-16"></div>
              <div className="h-5 bg-muted rounded w-20"></div>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  if (flights.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-8 text-center">
        <Plane className="h-12 w-12 mb-4 opacity-50" />
        <p className="text-sm">No flights added yet</p>
        <p className="text-xs mt-1">Search for airports above to get started</p>
      </div>
    );
  }

  return (
    <div className="space-y-2 p-4">
      {flights.length > 0 && (
        <h2 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wide">
          Your Flights ({flights.length})
        </h2>
      )}
      {flights.map((flight, index) => (
        <Card key={index} className="p-2 pr-16 relative group hover:bg-accent/5 hover:shadow-md transition-all duration-200">
          {/* Action Buttons */}
          <div className="absolute top-1.5 right-1.5 flex gap-1">
            {/* Mark as Flown Button - Only show when logged in */}
            {isLoggedIn && (
              <button
                onClick={() => onFlightFlown(index)}
                className="p-1 rounded-md hover:bg-green-500/20 text-muted-foreground hover:text-green-600 transition-colors"
                title="Mark as flown (adds points to current and deletes flight)"
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
              </button>
            )}
            {/* Delete Button */}
            <button
              onClick={() => onDeleteFlight(index)}
              className="p-1 rounded-md hover:bg-destructive/20 text-muted-foreground hover:text-destructive transition-colors"
              title="Remove flight"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Route and Cities - Single Line */}
          <div className="flex items-baseline gap-1.5 mb-1.5">
            <span className="text-base font-bold">{flight.origin.code}</span>
            <span className="text-muted-foreground text-xs">→</span>
            <span className="text-base font-bold">{flight.destination.code}</span>
            {flight.origin.city && flight.destination.city && (
              <span className="text-xs text-muted-foreground ml-1">
                {flight.origin.city} → {flight.destination.city}
              </span>
            )}
          </div>

          {/* Details - Inline Badges */}
          <div className="flex flex-wrap gap-1.5 text-xs">
            <Badge variant="outline" className="font-normal px-1.5 py-0 text-xs">
              {flight.distance_miles.toLocaleString()} mi
            </Badge>
            <Badge variant="secondary" className="font-normal capitalize px-1.5 py-0 text-xs">
              {flight.fare_class === 'custom' ? `Custom ${Math.round(flight.custom_multiplier * 100)}%` : flight.fare_class}
            </Badge>
            <Badge variant="default" className="bg-accent text-accent-foreground font-semibold px-1.5 py-0 text-xs">
              {flight.status_points.toLocaleString()} pts
            </Badge>
          </div>
        </Card>
      ))}
    </div>
  );
}
